import AbstractContract     from "@/domains/Contracts/AbstractContract";
import Core                 from "@/core/Core";
import Partner              from "@/domains/Partners/Partner";
import Company              from "@/domains/Companies/Company";
import DateRange            from "@/core/types/DateRange";
import DeliveryType         from "@/domains/DeliveryTypes/DeliveryType";
import DateSimple           from "@/core/types/DateSimple";

import type OrderRequisites from "@/domains/Orders/OrderRequisites";

import Title_ru_RU       from "@/core/misc/decorators/Title_ru_RU";
import TYPES             from "@/core/services/Changelogs/types";
import Title_ru_RU_Class from "@/core/misc/decorators/Title_ru_RU_Class";

/* Договор между покупатель и поставщиком, external исторически сложилось */
@Title_ru_RU_Class('Договор')
class ContractExternal extends AbstractContract {

  static Events = {
    Created: 'contractExternal:created',
    Updated: 'contractExternal:updated',
    Deleted: 'contractExternal:deleted'
  } as const;

  @Title_ru_RU('Номер договора', TYPES.NUMBER)
  public number: string;

  @Title_ru_RU('Поставщик', TYPES.OBJECT)
  public seller: Partner;
  public sellerCompany: Company;

  @Title_ru_RU('Покупатель', TYPES.OBJECT)
  public buyer: Partner;
  public buyerCompany: Company;

  @Title_ru_RU('Дата', TYPES.DATE_RANGE)
  public validityPeriod: DateRange;

  @Title_ru_RU('Тип доставки', TYPES.OBJECT)
  public deliveryType: DeliveryType;

  @Title_ru_RU('Покупатель оплачивает доставку', TYPES.BOOLEAN)
  public isDeliveryPaidByBuyer: boolean = false;

  /* включен ли НДС в цены поставщика */
  @Title_ru_RU('НДС', TYPES.BOOLEAN)
  public isVatIncluded: boolean | null;

  /**
   * Создаёт новый договор для указанного поставщика
   */
  static fromPartner(partner: Partner): ContractExternal {
    const contract = new this();
    contract.seller = partner;
    contract.sellerCompany = partner.companies.length === 1 ? partner.companies[0] : null;

    contract.validityPeriod = new DateRange(new DateSimple(), null);

    return contract;
  }

  /**
   * Создаёт новый договор по реквизитам заказа:
   * стороны и их юр. лица берутся из заказа как есть
   */
  static fromOrderRequisites(requisites: OrderRequisites): ContractExternal {
    const contract = new this();
    contract.seller        = requisites.seller;
    contract.sellerCompany = requisites.sellerCompany;
    contract.buyer         = requisites.buyer;
    contract.buyerCompany  = requisites.buyerCompany;

    contract.validityPeriod = new DateRange(new DateSimple(), null);

    return contract;
  }

  /**
   * Гидрация договора из ответа сервера
   */
  static fromServer(data: any, result = new ContractExternal()): ContractExternal {
    super.fromServer(data, result);

    result.number = data.number;
    result.isDeliveryPaidByBuyer = data.isDeliveryPaidByBuyer;
    result.isVatIncluded         = data.isVatIncluded;

    result.seller = Core.utils.hydrate(data.seller, Partner.fromServer);
    result.sellerCompany = Core.utils.hydrate(data.sellerCompany, Company.fromServer);

    result.buyer = Core.utils.hydrate(data.buyer, Partner.fromServer);
    result.buyerCompany = Core.utils.hydrate(data.buyerCompany, Company.fromServer);

    result.validityPeriod = Core.utils.hydrate(data.validityPeriod, DateRange.fromServer);
    result.deliveryType = Core.utils.hydrate(data.deliveryType, DeliveryType.fromServer);

    return result;
  }

  /**
   * Текстовая сводка условий оплаты для отображения в ячейке/карточке
   */
  get paymentTermsText(): string {
    if (this.prepaymentTerms.length > 0 && this.postpaymentTerms.length > 0) return 'Есть предоплата и постоплата';
    if (this.prepaymentTerms.length > 0)  return 'Есть предоплата';
    if (this.postpaymentTerms.length > 0) return 'Есть постоплата';

    return 'Отсутствуют';
  }

  /** Тип + номер + дата начала действия; чего нет (у нового договора) — пропускается */
  toString(): string {
    const type   = (this.constructor as any).entityTypeName;
    const number = this.number ? `№${this.number}` : '';
    const date   = this.validityPeriod?.start ? `от ${this.validityPeriod.start.format('d.m.Y')}` : '';

    return `${type} ${number} ${date}`.trim();
  }
}

export default ContractExternal;
